import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, startTransition } from 'react'
import { HistoryCache } from '../lib/idb-cache'
import { useMarketStore } from '../services/store'
import { API_CONFIG, getCanonicalSymbol, getStrikeStepForSymbol } from '../config/constants'
import { getFirebaseAuth } from '../services/firebase'
import { useSettingsStore } from '../stores/settingsStore'
import { useCogEngine } from '../hooks/useCogEngine'
import { useMarketWatchEngine } from '../hooks/useMarketWatchEngine'
import { usePrecomputeEngine } from '../hooks/usePrecomputeEngine'
import { useAuth } from '../auth/AuthContext'
import { prewarmDuckDB } from '../lib/duckdb-engine'


// FREEZE FIX: Stable singleton for empty results.
// Returning `[]` creates a NEW reference every call, causing React Query
// to think data changed → triggers re-renders → Zustand updates → freeze.
// Using a frozen singleton ensures referential stability.
const EMPTY_HISTORY: any[] = Object.freeze([]) as unknown as any[]

/**
 * Normalizes date string into canonical YYYY-MM-DD format for chronological comparison
 */
function toCanonicalDateStr(rawDate: string | number | undefined): string | null {
    if (!rawDate) return null;
    if (typeof rawDate === 'number') {
        const ms = rawDate < 1e11 ? rawDate * 1000 : rawDate;
        if (ms > 946684800000) {
            const d = new Date(ms + 19800000); // IST offset +5:30
            return d.toISOString().split('T')[0] || null;
        }
        return null;
    }
    const str = String(rawDate).trim();
    // Numeric string epoch (seconds or ms)
    if (/^\d{10,13}$/.test(str)) {
        const num = Number(str);
        const ms = num < 1e11 ? num * 1000 : num;
        if (ms > 946684800000) {
            const d = new Date(ms + 19800000);
            return d.toISOString().split('T')[0] || null;
        }
    }
    // YYYY-MM-DD
    const isoMatch = str.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    // DD-MM-YYYY or DD/MM/YYYY
    const dmyMatch = str.match(/\b(\d{2})[-/](\d{2})[-/](\d{4})\b/);
    if (dmyMatch) return `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`;
    return null;
}

/**
 * Extracts canonical YYYY-MM-DD date from a snapshot
 */
function extractSnapshotDate(snap: any): string | null {
    if (!snap) return null;
    return toCanonicalDateStr(snap.trade_date) ||
           toCanonicalDateStr(snap.traded_date) ||
           toCanonicalDateStr(snap.tradeDate) ||
           toCanonicalDateStr(snap.date) ||
           toCanonicalDateStr(snap.dateStr) ||
           toCanonicalDateStr(snap.timestampIST) ||
           toCanonicalDateStr(snap.timestamp_ist) ||
           toCanonicalDateStr(snap.timestamp) ||
           toCanonicalDateStr(snap.time);
}

/**
 * Filter and tag snapshots for 2 available dates:
 * - Does NOT check against today's calendar date.
 * - Finds distinct available dates in the dataset.
 * - The latest date is marked as Current / Selected day (isPD = false, dayType = 'CURRENT').
 * - The previous date is marked as Previous Day PD with available times (isPD = true, dayType = 'PD', time = "PD HH:mm").
 * - Any dates older than these 2 are filtered out.
 */
export function filterAndTagLatestAndPrevDay(snapshots: any[]): any[] {
    if (!Array.isArray(snapshots) || snapshots.length === 0) return snapshots || [];

    // 1. Collect all distinct canonical dates in chronological order
    const dateMap = new Map<string, string>(); // canonical -> raw/display
    for (const snap of snapshots) {
        if (!snap) continue;
        const cDate = extractSnapshotDate(snap);
        if (cDate && !dateMap.has(cDate)) {
            const rawDisplay = snap.date || snap.trade_date || snap.traded_date || cDate;
            dateMap.set(cDate, String(rawDisplay));
        }
    }

    const sortedDates = Array.from(dateMap.keys()).sort(); // ascending: e.g. ['2026-10-16', '2026-10-17']

    // If no dates identifiable, pass through with standard clean time
    if (sortedDates.length === 0) {
        return snapshots.map(s => {
            const rawTime = s.time || s.timestampIST || s.timestamp || '';
            const match = String(rawTime).match(/(\d{2}:\d{2})/);
            const cleanMin = match ? match[1]! : String(rawTime).trim().replace(/^PD\s*/, '');
            const isPD = !!s.isPD || String(s.time || '').startsWith('PD');
            const formattedTime = isPD ? `PD ${cleanMin}` : cleanMin;
            return {
                ...s,
                isPD,
                dayType: isPD ? 'PD' : 'CURRENT',
                time: formattedTime,
                timeStr: formattedTime,
                cleanTime: cleanMin
            };
        });
    }

    const latestCanonical = sortedDates[sortedDates.length - 1]!;
    const prevCanonical = sortedDates.length >= 2 ? sortedDates[sortedDates.length - 2]! : null;

    const filtered: any[] = [];
    for (const snap of snapshots) {
        if (!snap) continue;
        const cDate = extractSnapshotDate(snap);
        // If dates exist, only keep latest and previous date
        if (cDate && cDate !== latestCanonical && cDate !== prevCanonical) {
            continue; // Skip older dates
        }

        const isPD = (cDate === prevCanonical) || (cDate === null && (!!snap.isPD || String(snap.time || '').startsWith('PD')));
        const rawTime = snap.time || snap.timestampIST || snap.timestamp || '';
        const match = String(rawTime).match(/(\d{2}:\d{2})/);
        const cleanMin = match ? match[1]! : String(rawTime).trim().replace(/^PD\s*/, '');
        const formattedTime = isPD ? `PD ${cleanMin}` : cleanMin;

        filtered.push({
            ...snap,
            isPD,
            dayType: isPD ? 'PD' : 'CURRENT',
            date: cDate ? dateMap.get(cDate) : snap.date,
            canonicalDate: cDate,
            time: formattedTime,
            timeStr: formattedTime,
            cleanTime: cleanMin
        });
    }

    return filtered;
}

/**
 * Deduplicate snapshots by minute timestamp (HH:mm) considering PD vs CURRENT day.
 * Ensures that multiple records within the same minute never create duplicate bars or timeline distortion,
 * while preserving both Previous Day and Current Day at the same minute.
 */
function deduplicateSnapshots(snapshots: any[]): any[] {
    if (!Array.isArray(snapshots) || snapshots.length <= 1) return snapshots || [];
    const seenTimes = new Set<string>();
    const unique: any[] = [];
    for (const snap of snapshots) {
        if (!snap) continue;
        const rawTime = snap.cleanTime || snap.time || snap.timestampIST || snap.timestamp || '';
        const match = String(rawTime).match(/(\d{2}:\d{2})/);
        const minuteKey = match ? match[1]! : String(rawTime).trim().replace(/^PD\s*/, '');
        const datePrefix = snap.isPD ? 'PD_' : 'CUR_';
        const dedupKey = minuteKey ? `${datePrefix}${minuteKey}` : `${datePrefix}${String(rawTime).trim()}`;
        if (dedupKey && !seenTimes.has(dedupKey)) {
            seenTimes.add(dedupKey);
            unique.push(snap);
        } else if (!minuteKey) {
            unique.push(snap);
        }
    }

    // Stable chronological sort: PD first (ascending minutes), then CURRENT (ascending minutes)
    unique.sort((a, b) => {
        if (a.isPD !== b.isPD) {
            return a.isPD ? -1 : 1;
        }
        const tA = a.cleanTime || a.time || '';
        const tB = b.cleanTime || b.time || '';
        return tA.localeCompare(tB);
    });

    return unique;
}

// --- Web Worker Setup ---
// Instantiate a single global worker instance for the app lifecycle
import HistoryWorker from '../workers/history.worker.ts?worker';

let _historyWorker: Worker | null = null;
export const getHistoryWorker = () => {
    if (typeof window === 'undefined') return null; // SSR safety
    if (!_historyWorker) {
        _historyWorker = new HistoryWorker();
    }
    return _historyWorker;
};

// Helper to wrap postMessage in a Promise with leak-proof event listener cleanup
export const runInWorker = (type: string, payload: any): Promise<any> => {
    return new Promise((resolve, reject) => {
        const worker = getHistoryWorker();
        if (!worker) return reject(new Error('No worker available'));
        
        const id = Math.random().toString(36).substring(2, 15);
        
        let chunks: any[] = [];
        let isChunked = false;
        let baseResult: any = null;
        let timeoutId: any = null;

        const cleanup = () => {
            if (timeoutId) clearTimeout(timeoutId);
            worker.removeEventListener('message', handleMessage);
        };
        
        const handleMessage = (e: MessageEvent) => {
            if (e.data.id === id) {
                if (e.data.type === 'CHUNK') {
                    isChunked = true;
                    if (e.data.chunkIndex === 0 && e.data.baseResult) {
                        baseResult = e.data.baseResult;
                    }
                    if (e.data.data) {
                        chunks.push(...e.data.data);
                    }
                    // Traditional single-array chunking
                    if (e.data.totalChunks > 0 && e.data.chunkIndex === e.data.totalChunks - 1) {
                        cleanup();
                        const result = baseResult ? { ...baseResult, data: chunks } : chunks;
                        setTimeout(() => resolve(result), 0);
                    }
                } else if (e.data.type === 'CHUNK_PART') {
                    // For complex object chunks (e.g. MULTIPART)
                    if (baseResult && e.data.partKey) {
                        const keys = e.data.partKey.split('.');
                        let target = baseResult;
                        for (let i = 0; i < keys.length - 1; i++) {
                            target = target[keys[i]];
                        }
                        const lastKey = keys[keys.length - 1];
                        if (!Array.isArray(target[lastKey])) target[lastKey] = [];
                        target[lastKey].push(...e.data.data);
                    }
                } else if (e.data.type === 'SUCCESS_MULTIPART') {
                    cleanup();
                    resolve(baseResult);
                } else if (e.data.type === 'SUCCESS') {
                    cleanup();
                    if (e.data.encoding === 'json-string' && typeof e.data.data === 'string') {
                        try { resolve(JSON.parse(e.data.data)); }
                        catch (err) { reject(err); }
                    } else {
                        resolve(e.data.data);
                    }
                } else if (e.data.type === 'ERROR') {
                    cleanup();
                    reject(new Error(e.data.error || 'Unknown worker error'));
                }
            }
        };

        // 60-Second Safety Timeout to prevent dangling listeners (DuckDB WASM can take time)
        timeoutId = setTimeout(() => {
            cleanup();
            reject(new Error(`Worker task "${type}" timed out after 60s`));
        }, 60000);
        
        worker.addEventListener('message', handleMessage);
        worker.postMessage({ id, type, payload });
    });
};

/**
 * GlobalHistoryFetcher — SINGLE source of truth for Intraday History data.
 *
 * Fetches history via direct API endpoints (no tRPC) every 60s (if live) or once (if historical).
 * Syncs to Zustand store so ALL tables, charts, and cards read from the same data.
 *
 * Fallback order for Intraday: KV → D1 → CSV (handled server-side)
 */
export function GlobalHistoryFetcher() {
    const { isAuthenticated, loading, firebaseUser } = useAuth()
    const symbol = useSettingsStore((state) => state.symbol)
    const expiry = useSettingsStore((state) => state.expiry)
    const baseTime = useSettingsStore((state) => state.baseTime)
    const range = useSettingsStore((state) => state.range)
    const mode = useSettingsStore((state) => state.mode)
    const isLive = useSettingsStore((state) => state.isLive)
    const historicalDate = useSettingsStore((state) => state.historicalDate)
    const inspectionSource = useSettingsStore((state) => state.inspectionSource)
    const rangeStartDate = useSettingsStore((state) => state.rangeStartDate)
    const rangeEndDate = useSettingsStore((state) => state.rangeEndDate)

    // BACKGROUND MATHEMATICAL ENGINE: Generates CoG globally    // Background Analytical Engines
    useCogEngine()
    useMarketWatchEngine()
    
    // AOT PRECOMPUTATION ENGINE: Computes timeline for buttery smooth 0ms playback
    usePrecomputeEngine()

    const queryClient = useQueryClient()
    
    // Initialize DuckDB WASM in the worker asynchronously as early as possible
    useEffect(() => {
        runInWorker('INIT_DUCKDB', {}).catch(e => {
            console.error('[GlobalHistoryFetcher] Failed to initialize DuckDB', e);
        });
    }, []);

    // FREEZE FIX: Guard to prevent repeated CSV worker execution.
    // Without this, every time React Query re-runs the queryFn (on refetch, on tab focus, etc.),
    // the worker is triggered again even though the CSV data hasn't changed.
    const csvFallbackTriggeredRef = useRef(false)
    // Reset the guard when symbol or expiry changes (new data expected)
    const csvFallbackKeyRef = useRef(`${symbol}|${expiry}`)
    if (csvFallbackKeyRef.current !== `${symbol}|${expiry}`) {
        csvFallbackKeyRef.current = `${symbol}|${expiry}`
        csvFallbackTriggeredRef.current = false
    }

    // Track previous settings to detect user-driven changes and force refetch
    const prevSettings = useRef({
        symbol,
        expiry,
        isLive,
        historicalDate,
        inspectionSource,
        rangeStartDate,
        rangeEndDate,
    })

    // Track D1 availability so we never waste 3.5s timing out on every 1-min fetch
    const d1AvailableRef = useRef<boolean | null>(null)

    useEffect(() => {
        const prev = prevSettings.current
        const changed =
            prev.symbol !== symbol ||
            prev.expiry !== expiry ||
            prev.isLive !== isLive ||
            prev.historicalDate !== historicalDate ||
            prev.inspectionSource !== inspectionSource ||
            prev.rangeStartDate !== rangeStartDate ||
            prev.rangeEndDate !== rangeEndDate

        if (changed) {
            d1AvailableRef.current = null
            // ---> CLEAR STALE DATA IMMEDIATELY <---
            // If symbol changed, clear the old one. If not, clear the current one.
            // This immediately drops the tables/charts into a "loading" or "empty" state 
            // so the user visually sees that new data is fetching, preventing them from
            // manually refreshing the page out of confusion.
            // FREEZE FIX: Replaced startTransition with setTimeout to prevent React 18 'Expected static flag was missing'
            // tearing errors caused by mutating external Zustand stores during a concurrent transition.
            setTimeout(() => {
                useMarketStore.getState().clearHistoryForSymbol(prev.symbol)
                if (symbol !== prev.symbol) {
                    useMarketStore.getState().clearHistoryForSymbol(symbol)
                }
            }, 0)

            console.log(`[GlobalHistoryFetcher] Settings changed, switching history queries`)
            prevSettings.current = {
                symbol,
                expiry,
                isLive,
                historicalDate,
                inspectionSource,
                rangeStartDate,
                rangeEndDate,
            }
        }
    }, [
        symbol,
        expiry,
        isLive,
        historicalDate,
        inspectionSource,
        rangeStartDate,
        rangeEndDate,
        queryClient,
    ])

    const { data } = useQuery({
        queryKey: [
            'global-history',
            symbol,
            expiry,
            inspectionSource,
            historicalDate,
            rangeStartDate,
            rangeEndDate,
            isLive,
            !!firebaseUser,
        ],
        queryFn: async () => {
            if (!symbol) return null
            if (typeof window !== 'undefined' && window.location.pathname.includes('/backtest')) {
                return EMPTY_HISTORY; // Disable fetching on backtest page
            }

            // Mode 1: Current Intraday — Cloudflare D1 Primary with Google Sheet CSV Fallback (Sub-2s Target)
            if (inspectionSource === 'CurrentIntraday') {
                const INTRADAY_SHEET_LINKS = API_CONFIG.SOURCES.INTRADAY_SHEET_LINKS
                const canonicalSym = (getCanonicalSymbol(symbol) || symbol) as string
                const fetchStartTime = performance.now()

                // Helper: DD-MM-YYYY to YYYY-MM-DD
                const altExpiry = expiry ? (
                    /^\d{2}-\d{2}-\d{4}$/.test(expiry) ? `${expiry.split('-')[2]}-${expiry.split('-')[1]}-${expiry.split('-')[0]}` :
                    /^\d{4}-\d{2}-\d{2}$/.test(expiry) ? `${expiry.split('-')[2]}-${expiry.split('-')[1]}-${expiry.split('-')[0]}` : ''
                ) : ''

                // 1. PRIMARY SOURCE: Cloudflare D1 Storage (/api/intraday-history-v2)
                if (d1AvailableRef.current !== false) {
                try {
                    // Non-blocking quick auth check (max 200ms)
                    const user = firebaseUser || getFirebaseAuth().currentUser
                    const token = user ? await Promise.race([
                        user.getIdToken(false).catch(() => ''),
                        new Promise<string>((res) => setTimeout(() => res(''), 200))
                    ]) : ''

                    const d1Url = `/api/intraday-history-v2?symbol=${encodeURIComponent(canonicalSym)}&expiry=${encodeURIComponent(expiry || '')}`
                    
                    // Fast 600ms timeout budget for D1 to prevent long fallback delays
                    const controller = new AbortController()
                    const timeoutId = setTimeout(() => controller.abort(), 600)

                    const d1Resp = await fetch(d1Url, {
                        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                        signal: controller.signal
                    }).catch(() => null)
                    clearTimeout(timeoutId)

                    if (d1Resp && d1Resp.ok) {
                        const d1Data = await d1Resp.json().catch(() => [])
                        if (Array.isArray(d1Data) && d1Data.length > 0) {
                            d1AvailableRef.current = true;
                            const d1Time = (performance.now() - fetchStartTime).toFixed(0)
                            const lastSync = d1Resp.headers.get('x-last-sync-time') || (new Date(Date.now() + 5.5 * 3600000).toISOString().replace('T', ' ').substring(0, 19) + ' IST')
                            console.log(`[GlobalHistoryFetcher] ⚡ Loaded ${d1Data.length} snapshots from Cloudflare D1 Primary for ${canonicalSym} in ${d1Time}ms | Last Sync: ${lastSync}`)
                            return deduplicateSnapshots(filterAndTagLatestAndPrevDay(d1Data))
                        }
                    }
                    d1AvailableRef.current = false;
                    const elapsed = (performance.now() - fetchStartTime).toFixed(0)
                    console.log(`[GlobalHistoryFetcher] ⚠️ D1 primary empty / timed out (${elapsed}ms). Switching to Google Sheet CSV Fallback...`)
                } catch (d1Err: any) {
                    d1AvailableRef.current = false;
                    const elapsed = (performance.now() - fetchStartTime).toFixed(0)
                    console.log(`[GlobalHistoryFetcher] ⚠️ D1 primary failed (${d1Err?.message || elapsed}ms). Switching to Google Sheet CSV Fallback...`)
                }
                }

                // 2. FALLBACK SOURCE: Google Sheet CSV via Worker
                const csvUrl = (INTRADAY_SHEET_LINKS as any)[canonicalSym]
                if (!csvUrl) return EMPTY_HISTORY

                try {
                    console.log(`[GlobalHistoryFetcher] Fetching live Intraday CSV from Google Sheet for ${canonicalSym} expiry: ${expiry}...`)
                    
                    const minuteBucket = Math.floor(Date.now() / 60000);
                    const liveCsvUrl = `${csvUrl}&_t=${minuteBucket}`;
                    const resultObj = await runInWorker('FETCH_AND_PARSE_CSV_INTRADAY', {
                        csvUrl: liveCsvUrl,
                        canonicalSym,
                        expiry: expiry || '',
                        altExpiry
                    });

                    const groupedRows = Array.isArray(resultObj) ? resultObj : (resultObj.data || []);
                    const totalTime = (performance.now() - fetchStartTime).toFixed(0)
                    const lastSync = new Date(Date.now() + 5.5 * 3600000).toISOString().replace('T', ' ').substring(0, 19) + ' IST'
                    console.log(`[GlobalHistoryFetcher] Intraday CSV parsed ${groupedRows.length} snapshots for ${canonicalSym} in ${totalTime}ms | Last Sync: ${lastSync}`)
                    return deduplicateSnapshots(filterAndTagLatestAndPrevDay(groupedRows.length > 0 ? groupedRows : EMPTY_HISTORY))
                } catch (csvErr: any) {
                    console.error(`[GlobalHistoryFetcher] Intraday CSV fetch failed:`, csvErr?.message)
                    return EMPTY_HISTORY
                }
            }

            // Mode 2: Historical Snapshot (Single Day from Hugging Face -> Fallback D1)
            if (inspectionSource === 'HistoricalSnapshot' && historicalDate) {
                const canonicalSym = (getCanonicalSymbol(symbol) || symbol) as string

                const hfUrl = `/api/history-proxy?symbol=${canonicalSym}&date=${historicalDate}&type=parquet`

                try {
                    // Reset dataset date before fetching to prevent displaying stale date badges
                    useSettingsStore.getState().setDatasetDate(null);
                    let groupedRows: any[] = [];
                    let finalExpiryList: string[] = [];
                    let autoSelectedExpiry: string | null = null;

                    let fetchFromDb = false;
                    try {
                        const hfToken = (await firebaseUser?.getIdToken()) || (await getFirebaseAuth().currentUser?.getIdToken()) || "";
                        console.log(`[GlobalHistoryFetcher] Offloading HF Fetch & Parse to Web Worker -> ${hfUrl}`)
                        
                        const workerResult = await runInWorker('FETCH_AND_PARSE_PARQUET_HF', {
                            hfUrl,
                            hfToken,
                            canonicalSym,
                            historicalDate,
                            expiry: expiry || ''
                        });
                        
                        groupedRows = workerResult.data;
                        finalExpiryList = workerResult.finalExpiryList;
                        autoSelectedExpiry = workerResult.autoSelectedExpiry;

                        if (groupedRows.length > 0 && workerResult.datasetDate) {
                            useSettingsStore.getState().setDatasetDate(workerResult.datasetDate);
                        } else {
                            useSettingsStore.getState().setDatasetDate(null);
                        }

                        if (groupedRows.length === 0) {
                            console.log(`[GlobalHistoryFetcher] HF Parquet returned 0 snapshots for ${canonicalSym}/${historicalDate}. Trying DB fallback...`);
                            fetchFromDb = true;
                        }
                    } catch (hfErr: any) {
                        useSettingsStore.getState().setDatasetDate(null);
                        const msg = hfErr.message || '';
                        if (msg.includes('not found') || msg.includes('not_found') || msg.includes('HF_NOT_FOUND')) {
                            console.log(`[GlobalHistoryFetcher] Parquet file not present on HF (${canonicalSym}/${historicalDate}). Switching to DB fallback...`);
                        } else {
                            console.log(`[GlobalHistoryFetcher] HF Parquet loading fallback (${msg}). Trying DB fallback...`);
                        }
                        fetchFromDb = true;
                    }

                    if (fetchFromDb) {
                        const params = new URLSearchParams({
                            symbol: canonicalSym,
                            date: historicalDate,
                        });
                        const token = (await firebaseUser?.getIdToken()) || (await getFirebaseAuth().currentUser?.getIdToken()) || "";
                        let dbData: any[] = [];
                        try {
                            const dbResp = await fetch(`/api/historical?${params}`, {
                                headers: token ? { Authorization: `Bearer ${token}` } : undefined
                            });
                            if (dbResp.ok) {
                                const cType = dbResp.headers.get('content-type') || '';
                                if (cType.includes('application/json')) {
                                    dbData = await dbResp.json();
                                }
                            }
                        } catch (e) {
                            console.warn('[GlobalHistoryFetcher] /api/historical fetch failed:', e);
                        }

                        if (Array.isArray(dbData) && dbData.length > 0) {
                            console.log(`[GlobalHistoryFetcher] DB/Intraday fallback returned ${dbData.length} snapshots.`);
                            groupedRows = dbData;
                            // Extrapolate and sort finalExpiryList from DB records so MIN expiry is first
                            const expiries = Array.from(new Set(dbData.flatMap((r: any) => {
                                if (r.expiry) return [r.expiry];
                                if (r.optionsData) {
                                    const sym = Object.keys(r.optionsData)[0];
                                    if (sym && r.optionsData[sym]) return Object.keys(r.optionsData[sym]);
                                }
                                return [];
                            }).filter(Boolean))) as string[];
                            if (expiries.length > 0) {
                                expiries.sort((a, b) => {
                                    const pA = a.split('-');
                                    const pB = b.split('-');
                                    const dA = new Date(Number(pA[2]||0), Number(pA[1]||1)-1, Number(pA[0]||0)).getTime();
                                    const dB = new Date(Number(pB[2]||0), Number(pB[1]||1)-1, Number(pB[0]||0)).getTime();
                                    return dA - dB;
                                });
                                finalExpiryList = expiries;
                                autoSelectedExpiry = finalExpiryList.includes(expiry || '') ? expiry : finalExpiryList[0] || '';
                            }
                        }
                    }
                        
                    // Sync available HF expiries to store so dropdown shows correct options
                    if (finalExpiryList.length > 0) {
                        const canonSymForStore = (getCanonicalSymbol(symbol) || symbol) as string
                        setTimeout(() => {
                            useMarketStore.getState().setExpiryForSymbol(canonSymForStore, finalExpiryList)
                        }, 0)
                    }
                    
                    // Select target expiry: use current if matches, otherwise MIN from file
                    if (autoSelectedExpiry && autoSelectedExpiry !== expiry) {
                        console.log(`[GlobalHistoryFetcher] Auto-selecting HF expiry: ${autoSelectedExpiry}`)
                        queryClient.setQueryData([
                            'global-history',
                            symbol,
                            autoSelectedExpiry,
                            inspectionSource,
                            historicalDate,
                            rangeStartDate,
                            rangeEndDate,
                            isLive,
                            !!firebaseUser,
                        ], groupedRows)
                        setTimeout(() => useSettingsStore.getState().setExpiry(autoSelectedExpiry), 0)
                    }

                    console.log(`[GlobalHistoryFetcher] HF Worker processing succeeded. -> ${groupedRows.length} snapshots. Available expiries: [${finalExpiryList.join(', ')}]`)

                    // Intelligent Background Pre-fetching of Adjacent Traded Day during Browser Idle Time
                    if (typeof window !== 'undefined' && historicalDate) {
                        const idleFn = (window as any).requestIdleCallback || ((cb: any) => setTimeout(cb, 3000));
                        idleFn(() => {
                            try {
                                const curDate = new Date(historicalDate);
                                const prevDate = new Date(curDate);
                                prevDate.setDate(prevDate.getDate() - 1);
                                if (prevDate.getDay() === 0) prevDate.setDate(prevDate.getDate() - 2);
                                else if (prevDate.getDay() === 6) prevDate.setDate(prevDate.getDate() - 1);

                                const yyyy = prevDate.getFullYear();
                                const mm = String(prevDate.getMonth() + 1).padStart(2, '0');
                                const dd = String(prevDate.getDate()).padStart(2, '0');
                                const prevDateStr = `${yyyy}-${mm}-${dd}`;

                                const prefetchHfUrl = `/api/history-proxy?symbol=${canonicalSym}&date=${prevDateStr}&type=parquet`;
                                getFirebaseAuth().currentUser?.getIdToken(false).then((token) => {
                                    runInWorker('PREFETCH_ADJACENT_PARQUET', {
                                        hfUrl: prefetchHfUrl,
                                        hfToken: token || '',
                                        canonicalSym,
                                        date: prevDateStr,
                                    }).catch(() => {});
                                }).catch(() => {});
                            } catch {}
                        });
                    }

                    return deduplicateSnapshots(filterAndTagLatestAndPrevDay(groupedRows))
                } catch (err: any) {
                    console.error(`[GlobalHistoryFetcher] Hugging Face failed (${err.message}). No fallback allowed in Historical mode.`);
                    throw err; // React Query will handle the error state
                }
            }

            // Mode 3: Range (Intraday or EOD from D1)
            if (
                (inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD') &&
                rangeStartDate &&
                rangeEndDate
            ) {
                const params = new URLSearchParams({
                    symbol,
                    start: rangeStartDate,
                    end: rangeEndDate,
                    isEod: inspectionSource === 'RangeEOD' ? 'true' : 'false',
                })
                if (expiry) params.set('expiry', expiry)
                const token = (await firebaseUser?.getIdToken()) || (await getFirebaseAuth().currentUser?.getIdToken()) || "";
                const resp = await fetch(`/api/range-history?${params}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined
                })
                if (!resp.ok) throw new Error(`Range history fetch failed: ${resp.status}`)
                const contentType = resp.headers.get('content-type') || ''
                if (!contentType.includes('application/json')) {
                    throw new Error(`Range history returned non-JSON (${resp.status})`)
                }
                const rangeData = await resp.json()
                return deduplicateSnapshots(filterAndTagLatestAndPrevDay(Array.isArray(rangeData) ? rangeData : []))
            }

            return null
        },
        enabled: !!symbol && isAuthenticated && !loading && !!firebaseUser,
        refetchInterval: (query) => (isLive && inspectionSource === 'CurrentIntraday' ? 60_000 : false),
        refetchIntervalInBackground: false,
        // Data is fresh for 45s in live mode
        staleTime: isLive && inspectionSource === 'CurrentIntraday' ? 45_000 : Infinity,
        gcTime: isLive ? 180_000 : Infinity, // Keep cache 3 min
        placeholderData: (prev: any) => prev,
        retry: 1,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
    })

    const lastWakeRef = useRef<number>(0)

    // Instant Intraday History Invalidation upon returning to tab (throttled)
    useEffect(() => {
        // Pre-warm DuckDB WASM during idle time
        prewarmDuckDB()

        const handleWake = () => {
            if (document.visibilityState === 'visible') {
                const now = Date.now()
                if (now - lastWakeRef.current < 60000) return // 60s cooldown prevents redundant tab wake fetches
                if (queryClient.isFetching({ queryKey: ['global-history'] }) > 0) return
                lastWakeRef.current = now

                const settings = useSettingsStore.getState()
                if (settings.isLive && settings.inspectionSource === 'CurrentIntraday' && settings.symbol) {
                    console.log(`[GlobalHistoryFetcher] Tab became active/visible. Refreshing Intraday History...`)
                    queryClient.invalidateQueries({
                        queryKey: ['global-history'],
                    })
                }
            }
        }

        document.addEventListener('visibilitychange', handleWake)
        window.addEventListener('focus', handleWake)
        window.addEventListener('pageshow', handleWake)
        window.addEventListener('online', handleWake)

        return () => {
            document.removeEventListener('visibilitychange', handleWake)
            window.removeEventListener('focus', handleWake)
            window.removeEventListener('pageshow', handleWake)
            window.removeEventListener('online', handleWake)
        }
    }, [queryClient])

    // Track last processed data signature to prevent redundant processing on identical data
    const lastProcessedRef = useRef<string>('')
    // Track last computed key to prevent duplicate COMPUTE_ALL dispatches
    const lastComputedKeyRef = useRef<string>('')

    // Sync to appropriate store slice
    useEffect(() => {
        if (!data) return
        if (Array.isArray(data) && data.length === 0) {
            return
        }

        // Content Signature Guard: Prevent running heavy WebWorker decompression & COMPUTE_ALL when data content hasn't changed
        const firstRow = Array.isArray(data) && data.length > 0 ? data[0] : null
        const lastRow = Array.isArray(data) && data.length > 0 ? data[data.length - 1] : null
        const dataSignature = `${symbol}_${expiry}_${inspectionSource}_${data?.length || 0}_${firstRow?.time || firstRow?.timestamp || ''}_${lastRow?.time || lastRow?.timestamp || ''}`

        if (lastProcessedRef.current === dataSignature) {
            return // Skip identical data completely
        }
        lastProcessedRef.current = dataSignature

        // Capture symbol at invocation time so we can abort if it changes mid-processing
        const capturedSymbol = symbol

        ;(async () => {
            try {
                let processedData = data;
                
                // Only offload to worker if we actually have compressed blobs
                // CSV fallback data is already parsed by the worker in the query step
                if (data.length > 0 && (data[0].data_blob || data[0].dataBlob)) {
                    console.time('[PERF] GlobalHistoryFetcher.WebWorkerProcess')
                    console.log(`[GlobalHistoryFetcher] Processing ${data.length} raw history entries for ${capturedSymbol} via Web Worker...`);
                    // Offload CBOR+GZIP Base64 decompression to the worker
                    processedData = await runInWorker('PROCESS_COMPRESSED', { rows: data })
                    console.timeEnd('[PERF] GlobalHistoryFetcher.WebWorkerProcess')
                } else {
                    console.log(`[GlobalHistoryFetcher] Skipping Web Worker decompression (Data is already processed)`);
                }

                // Ensure 2-date filtering & tagging (Latest Day vs Previous Day PD) is applied
                processedData = deduplicateSnapshots(filterAndTagLatestAndPrevDay(processedData));

                // Abort if symbol changed during async processing
                if (symbol !== capturedSymbol) {
                    console.log(`[GlobalHistoryFetcher] Symbol changed (${capturedSymbol} → ${symbol}), discarding stale results.`)
                    return
                }

                console.log(`[GlobalHistoryFetcher] Successfully processed ${processedData.length} records. Syncing to DataBus (${inspectionSource}).`);
                
                // 🚀 SSOT: Dispatch COMPUTE_ALL — single Worker call for both COG + MarketWatch + AnalysisHistory
                // This replaces 3 independent Worker calls (3x Structured Clone) with 1 (1x Structured Clone)
                const lastSnap = Array.isArray(processedData) && processedData.length > 0 ? processedData[processedData.length - 1] : null
                const lastSnapTime = lastSnap?.time || lastSnap?.timestamp || lastSnap?.timestampIST || ''
                const computeKey = `${capturedSymbol}-${expiry}-${range}-${mode}-${processedData.length}-${lastSnapTime}`
                if (lastComputedKeyRef.current === computeKey) return // Already computed
                lastComputedKeyRef.current = computeKey

                const step = getStrikeStepForSymbol(capturedSymbol)
                const settings = useSettingsStore.getState()
                const mwSubTab = settings.subTab || 'Combined OI'
                const computeStartTime = performance.now()

                // Mark computing in-flight to prevent duplicate worker dispatches from hooks
                useMarketStore.getState().setIsComputingHistory(capturedSymbol, true)

                const result = await runInWorker('COMPUTE_ALL', {
                    history: processedData,
                    symbol: capturedSymbol,
                    expiry: expiry || '',
                    step,
                    range: range || 11,
                    strikeGap: step,
                    activeRange: range || 11,
                    cogSubTab: 'Top CE / PE OI',
                    mwSubTab,
                    isVolumeMode: mode === 'VOLUME',
                    baseOpts: [],
                    mode: mode,
                    baseTimePattern: baseTime,
                })
                const durationMs = (performance.now() - computeStartTime).toFixed(1)
                console.log(`[GlobalHistoryFetcher] COMPUTE_ALL executed in ${durationMs}ms for ${capturedSymbol}`)

                // Abort if symbol changed during compute
                if (symbol !== capturedSymbol) {
                    useMarketStore.getState().setIsComputingHistory(capturedSymbol, false)
                    return
                }

                // 🚀 PERF: Single atomic store commit wrapped in startTransition.
                // Commits raw history + COG + MarketWatch + AnalysisHistory all together in ONE store update.
                // ZERO intermediate states, ZERO race conditions, ZERO duplicate worker calls!
                if (result) {
                    // Pre-compute COG dedup BEFORE entering the store mutation
                    let finalCog: any[] | null = null
                    if (result.cogHistory && Array.isArray(result.cogHistory)) {
                        const cogMap = new Map<string, any>()
                        for (const r of result.cogHistory) cogMap.set(r.time, r)
                        finalCog = Array.from(cogMap.values()).sort((a, b) => b.time.localeCompare(a.time))
                    }

                    // Map inspectionSource → store history bucket
                    const historySource = inspectionSource === 'CurrentIntraday' ? 'intraday' as const
                        : inspectionSource === 'HistoricalSnapshot' ? 'historical' as const
                        : 'range' as const

                    startTransition(() => {
                        useMarketStore.getState().setMasterComputeAll(
                            capturedSymbol,
                            historySource,
                            processedData,
                            finalCog,
                            result.marketWatchHistory || null,
                            result.analysisHistory || null
                        )
                    })
                    console.log(`[GlobalHistoryFetcher] COMPUTE_ALL completed for ${capturedSymbol}. COG: ${finalCog?.length || 0} rows, MW: ${result.marketWatchHistory?.processedHistory?.length || 0} rows`)
                } else {
                    useMarketStore.getState().setIsComputingHistory(capturedSymbol, false)
                }
            } catch (err) {
                useMarketStore.getState().setIsComputingHistory(capturedSymbol, false)
                console.error('[GlobalHistoryFetcher] Failed to hydrate store:', err)
            }
        })()
    }, [data, symbol, inspectionSource, expiry, range, mode, baseTime])

    return null
}
